import { FC, KeyboardEvent, useState } from 'react';
import { Pencil } from 'lucide-react';

import { useUpdateTranscriptDetailsMutation, type TranscriptBreakdown } from '@teachur/http-client';
import { DescriptionList } from '@new/common/ui/DescriptionList/DescriptionList';
import { IconButton } from '@new/common/ui/IconButton/IconButton';
import { Input } from '@new/common/ui/Input/Input';
import { Textarea } from '@new/common/ui/Textarea/Textarea';

import {
  accreditingOrganizationsToText,
  sameAccreditingOrganizations,
  textToAccreditingOrganizations,
} from './accreditingOrganizations';

interface InstitutionDetailsEditorProps {
  transcript: TranscriptBreakdown;
}

const EDIT_CONTROL_CLASS =
  `h-9! min-h-0! rounded-md! border-[1.5px]! border-solid! ` +
  'border-(--neutral-white-NW800)! bg-(--neutral-white-NW900)! focus:border-(--neutral-white-NW800)! ' +
  'p-2! [&&]:[font-size:var(--fs-p4)]!';
const EDIT_INPUT_CLASS = EDIT_CONTROL_CLASS;
const EDIT_TEXTAREA_CLASS = `${EDIT_CONTROL_CLASS} h-auto! resize-none!`;

export const InstitutionDetailsEditor: FC<InstitutionDetailsEditorProps> = ({ transcript }) => {
  return (
    <>
      <InstitutionWebsiteField transcript={transcript} />
      <AccreditingOrganizationsField transcript={transcript} />
      <AwardedDegreeField transcript={transcript} />
    </>
  );
};

const InstitutionWebsiteField: FC<InstitutionDetailsEditorProps> = ({ transcript }) => {
  const updateDetails = useUpdateTranscriptDetailsMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const onEdit = () => {
    setDraft(transcript.institutionUrl ?? '');
    setEditing(true);
  };

  const onSave = () => {
    const next = draft.trim() || null;
    if (next !== (transcript.institutionUrl ?? null)) {
      updateDetails.mutate({ id: transcript.id, body: { institutionUrl: next } });
    }
    setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <DescriptionList.Row label="Institution website">
      {editing ? (
        <Input
          value={draft}
          onChange={setDraft}
          autoFocus
          onBlur={onSave}
          onKeyDown={onKeyDown}
          disabled={updateDetails.isPending}
          placeholder="Add institution website"
          className={EDIT_INPUT_CLASS}
        />
      ) : (
        <ReadOnlyValue
          value={transcript.institutionUrl}
          onEdit={onEdit}
          label="institution website"
        />
      )}
    </DescriptionList.Row>
  );
};

const AccreditingOrganizationsField: FC<InstitutionDetailsEditorProps> = ({ transcript }) => {
  const updateDetails = useUpdateTranscriptDetailsMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const onEdit = () => {
    setDraft(accreditingOrganizationsToText(transcript.accreditingOrganizations));
    setEditing(true);
  };

  const onSave = () => {
    const next = textToAccreditingOrganizations(draft);
    if (!sameAccreditingOrganizations(next, transcript.accreditingOrganizations)) {
      updateDetails.mutate({ id: transcript.id, body: { accreditingOrganizations: next } });
    }
    setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <DescriptionList.Row label="Accrediting organizations">
      {editing ? (
        <Textarea
          value={draft}
          onChange={setDraft}
          autoFocus
          onBlur={onSave}
          onKeyDown={onKeyDown}
          disabled={updateDetails.isPending}
          rows={Math.max(1, draft.split('\n').length)}
          placeholder="Add accrediting organization (one per line)"
          className={EDIT_TEXTAREA_CLASS}
        />
      ) : (
        <ReadOnlyValue
          value={accreditingOrganizationsToText(transcript.accreditingOrganizations) || null}
          onEdit={onEdit}
          label="accrediting organizations"
        />
      )}
    </DescriptionList.Row>
  );
};

const AwardedDegreeField: FC<InstitutionDetailsEditorProps> = ({ transcript }) => {
  const updateDetails = useUpdateTranscriptDetailsMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const onEdit = () => {
    setDraft(transcript.awardedDegree ?? '');
    setEditing(true);
  };

  const onSave = () => {
    const next = draft.trim() || null;
    if (next !== (transcript.awardedDegree ?? null)) {
      updateDetails.mutate({ id: transcript.id, body: { awardedDegree: next } });
    }
    setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <DescriptionList.Row label="Awarded degree">
      {editing ? (
        <Input
          value={draft}
          onChange={setDraft}
          autoFocus
          onBlur={onSave}
          onKeyDown={onKeyDown}
          disabled={updateDetails.isPending}
          placeholder="Add awarded degree"
          className={EDIT_INPUT_CLASS}
        />
      ) : (
        <ReadOnlyValue value={transcript.awardedDegree} onEdit={onEdit} label="awarded degree" />
      )}
    </DescriptionList.Row>
  );
};

interface ReadOnlyValueProps {
  value: string | null;
  label: string;
  onEdit: () => void;
}

const ReadOnlyValue: FC<ReadOnlyValueProps> = ({ value, label, onEdit }) => (
  <div className={`flex items-center justify-between gap-2 min-h-9`}>
    <span className="whitespace-pre-line">{value ?? '—'}</span>
    <IconButton
      size="sm"
      label={`Edit ${label}`}
      icon={<Pencil className="w-4 h-4" />}
      onClick={onEdit}
    />
  </div>
);
